/** @format */

import {
  AuthorizationResponse,
  Checkout,
  CommerceAddress,
  ContactForm,
  DirectCheckoutAddress,
  EDDv2,
  ExpressCheckoutAddressType,
  GiftCard,
  PaymentError,
} from '@shared/data-access/interfaces';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, defer, finalize, firstValueFrom, of, switchMap, tap, throwError } from 'rxjs';

import { CheckoutAnalyticsService } from './checkout.analytics.service';
import { CheckoutDirectDao } from './checkout-direct.dao';
import { CheckoutCreateRequest, CheckoutSwitchingService } from './checkout-switching.service';
import { ExpressCheckoutDrawerService } from './express-checkout-drawer.service';
import { LoadingOverlayService } from '../loading-overlay.service';
import { PaymentService } from './payment.service';
import { Store } from '../app.store';
import { catchError, take } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';

export const GIFT_CARD_UNAVAILABLE = 'Gift cards are not available on this order.';
export const SHIP_METHOD_UNAVAILABLE = 'Shipping options cannot be changed on this order.';

export interface ExpressCheckoutActionResult {
  status: 'SUCCESS' | 'Fail';
  checkout?: Checkout;
}

/** Ship mode request as the payments MFE sends it (snake_case). */
export interface PaymentsShipModeRequest {
  description: string;
  ship_method: string;
  unit_price: string;
  group_number?: number;
}

/**
 * @description
 * Owns the express ("Buy Now") checkout as a signal and is the single source of truth
 * the express drawer renders from. Also handles Independence Pay promo apply/remove for
 * the express flow, plus the gift card seam below, keeping the same promise contract the
 * payments MFE expects from the regular flow. Express promo endpoints live on CheckoutDirectDao.
 *
 * Uses tapResponse (@ngrx/operators) for unified next/error/finalize handling, bridged to a
 * Promise so the external window.dcsgCartCheckout.* caller keeps an awaitable result.
 * rxMethod is intentionally not used here — it returns void, which would break that contract.
 */
@Injectable({
  providedIn: 'root',
})
export class ExpressCheckoutStateService {
  private readonly checkoutAnalyticsService = inject(CheckoutAnalyticsService);
  private readonly checkoutDirectDao = inject(CheckoutDirectDao);
  private readonly checkoutSwitchingService = inject(CheckoutSwitchingService);
  private readonly store = inject(Store);
  private readonly loadingOverlayService = inject(LoadingOverlayService);
  private readonly paymentService = inject(PaymentService);

  private readonly expressCheckoutDrawerService = inject(ExpressCheckoutDrawerService);

  private checkoutBeforeSheet: Checkout | null = null;

  private readonly _checkout = signal<Checkout | null>(null);
  readonly checkout = this._checkout.asReadonly();

  setCheckout(checkout: Checkout | null): void {
    this._checkout.set(checkout);
    this.store.set('EXPRESS_CHECKOUT', checkout ?? undefined);

    // Drawer-scoped: the cart page owns ESTIMATED_DELIVERY, and only create/address/patch/
    // ship-mode responses restamp EDD.
    if (!checkout) {
      this.store.set('DIRECT_ESTIMATED_DELIVERY', undefined);
    } else if (checkout.checkout_estimated_delivery) {
      this.store.set('DIRECT_ESTIMATED_DELIVERY', checkout.checkout_estimated_delivery);
    }
  }

  clear(): void {
    this.checkoutBeforeSheet = null;
    this.setCheckout(null);
  }

  // Checkout actions route through CheckoutSwitchingService so the active flow decides the
  // endpoint set, and land their result in this service's signal rather than each caller
  // repeating setCheckout.
  createCheckout$ = (request: CheckoutCreateRequest): Observable<Checkout | null> =>
    this.checkoutSwitchingService
      .createCheckout$(request)
      .pipe(tap((checkout) => this.storeIfPresent(checkout)));

  addAddress$ = (
    contact: ContactForm,
    address: CommerceAddress,
    addressType?: ExpressCheckoutAddressType,
  ): Observable<Checkout | null> =>
    this.checkoutSwitchingService
      .addAddress$(this._checkout()?.checkout_key, contact, address, addressType)
      .pipe(tap((checkout) => this.storeIfPresent(checkout)));

  // Callers reject a null response themselves, but this tap runs upstream of them, so storing
  // it here would drop the checkout_key a retry needs. clear() is the deliberate reset.
  private storeIfPresent(checkout: Checkout | null): void {
    if (checkout) {
      this.setCheckout(checkout);
    }
  }

  submitCheckout$ = (): Observable<Checkout | null> =>
    this.checkoutSwitchingService.submitCheckout$(this._checkout()?.checkout_key);

  getSubmittedCheckout$ = (): Observable<Checkout | null> =>
    this.checkoutSwitchingService.getSubmittedCheckout$(this._checkout()?.checkout_key);

  /**
   * The Observables are the primitive the drawer uses: like the regular flow, a response carrying
   * `messaging` still emits, since the promo may have applied and the order messages surface it.
   * The promise twins keep the payments MFE contract, which treats `messaging` as a rejection.
   */
  public applyPromo$ = (promoCode: string): Observable<Checkout> =>
    this.promoAction$(
      promoCode,
      (checkoutKey) => this.checkoutSwitchingService.applyPromotion$(checkoutKey, promoCode),
      (checkout) =>
        checkout.messaging
          ? `Failed to Apply promo:${promoCode}`
          : `Successfully Applied Promo:${promoCode}`,
      `Failed to Apply promo:${promoCode}`,
    );

  public removePromo$ = (promoCode: string): Observable<Checkout> =>
    this.promoAction$(
      promoCode,
      (checkoutKey) => this.checkoutSwitchingService.removePromotion$(checkoutKey, promoCode),
      () => `Successfully Removed Promo:${promoCode}`,
      `Failed to Remove promo:${promoCode}`,
    );

  public applyPromo = (promoCode: string): Promise<ExpressCheckoutActionResult> =>
    firstValueFrom(this.applyPromo$(promoCode)).then((checkout) => {
      if (checkout.messaging) {
        throw this.toActionError(checkout.messaging.message);
      }
      return { status: 'SUCCESS', checkout };
    });

  public removePromo = (promoCode: string): Promise<ExpressCheckoutActionResult> =>
    firstValueFrom(this.removePromo$(promoCode)).then((checkout) => ({
      status: 'SUCCESS',
      checkout,
    }));

  private promoAction$(
    promoCode: string,
    request: (checkoutKey: string) => Observable<Checkout>,
    successEvent: (checkout: Checkout) => string,
    failureEvent: string,
  ): Observable<Checkout> {
    return defer(() => {
      this.loadingOverlayService.show();
      const checkoutKey = this._checkout()?.checkout_key;
      if (!checkoutKey) {
        this.loadingOverlayService.hide();
        return throwError(() => this.toActionError('No active checkout'));
      }
      return request(checkoutKey).pipe(
        take(1),
        tap((checkout: Checkout) => {
          this.setCheckout(checkout);
          this.checkoutAnalyticsService.triggerCheckoutActionEvent(successEvent(checkout));
        }),
        catchError((err: unknown) => {
          this.checkoutAnalyticsService.triggerCheckoutActionEvent(failureEvent);
          return throwError(() => this.toActionError(err));
        }),
        finalize(() => this.loadingOverlayService.hide()),
      );
    });
  }

  /**
   * Gift card is a direct-only action — /express has no counterpart — so it goes straight to
   * CheckoutDirectDao and is rejected while the express flow is active. The Observable is the
   * primitive; the promise twins below adapt it for the payments MFE's IndependencePay bridge.
   */
  public applyGiftCard$ = (giftCard: GiftCard): Observable<Checkout> => {
    const checkoutKey = this._checkout()?.checkout_key;
    if (!checkoutKey) {
      return this.giftCardUnavailable$('No active checkout');
    }
    if (!this.checkoutSwitchingService.isDirect()) {
      return this.giftCardUnavailable$(GIFT_CARD_UNAVAILABLE);
    }
    return this.checkoutDirectDao
      .applyGiftCard(checkoutKey, {
        cardNumber: giftCard?.card_number,
        pin: giftCard?.pin,
      })
      .pipe(
        tap((checkout: Checkout) => this.setCheckout(checkout)),
        finalize(() => this.loadingOverlayService.hide()),
      );
  };

  public removeGiftCard$ = (externalKey: string): Observable<Checkout> => {
    const checkoutKey = this._checkout()?.checkout_key;
    if (!checkoutKey) {
      return this.giftCardUnavailable$('No active checkout');
    }
    if (!this.checkoutSwitchingService.isDirect()) {
      return this.giftCardUnavailable$(GIFT_CARD_UNAVAILABLE);
    }
    // A gift card's `external_key` is its index stringified (PaymentV2Translator maps
    // `index` -> `external_key`), so it is already the path segment both endpoints expect.
    return this.checkoutDirectDao.deleteGiftCard(checkoutKey, externalKey).pipe(
      tap((checkout: Checkout) => this.setCheckout(checkout)),
      finalize(() => this.loadingOverlayService.hide()),
    );
  };

  public addProxyPickup$ = (
    orderId: string,
    proxyPickupInfo: ContactForm,
  ): Observable<Checkout> => {
    const directProxyPickupInfo = {
      firstName: proxyPickupInfo.first_name,
      lastName: proxyPickupInfo.last_name,
      email: proxyPickupInfo.email,
    };
    return this.checkoutDirectDao.addProxyPickup(orderId, directProxyPickupInfo).pipe(
      tapResponse({
        next: (checkout: Checkout) => {
          this.setCheckout(checkout);
        },
        error: (err: unknown) => {
          throw this.toActionError(err);
        },
        finalize: () => this.loadingOverlayService.hide(),
      }),
    );
  };

  public removeProxyPickup$ = (orderId: string): Observable<Checkout> => {
    return this.checkoutDirectDao.deleteProxyPickup(orderId).pipe(
      tapResponse({
        next: (checkout: Checkout) => {
          this.setCheckout(checkout);
        },
        error: (err: unknown) => {
          throw this.toActionError(err);
        },
        finalize: () => this.loadingOverlayService.hide(),
      }),
    );
  };

  public applyGiftCard = (giftCard: GiftCard): Promise<ExpressCheckoutActionResult> =>
    this.toActionPromise(this.applyGiftCard$(giftCard));

  public removeGiftCard = (externalKey: string): Promise<ExpressCheckoutActionResult> =>
    this.toActionPromise(this.removeGiftCard$(externalKey));

  public addProxyPickup = (
    orderId: string,
    proxyPickupInfo: ContactForm,
  ): Promise<ExpressCheckoutActionResult> =>
    this.toActionPromise(this.addProxyPickup$(orderId, proxyPickupInfo));

  public removeProxyPickup = (orderId: string): Promise<ExpressCheckoutActionResult> =>
    this.toActionPromise(this.removeProxyPickup$(orderId));

  /**
   * Payments seam: express-pay buttons (Apple Pay) drive the order through
   * window.dcsgCartCheckout, so these keep the regular flow's promise contract while
   * targeting the drawer's checkout instead of the Store's CHECKOUT key. As in the regular
   * flow, address and ship method changes go back to payments only; the drawer keeps showing
   * the checkout from before the sheet until the order is placed or the sheet is cancelled.
   */
  public getCheckoutForPayments = (): Promise<Checkout> => {
    const checkout = this._checkout();
    return checkout
      ? Promise.resolve(checkout)
      : Promise.reject(this.toActionError('No active checkout'));
  };

  public addAndFinalizeAddress = (
    addressType: string,
    address: DirectCheckoutAddress,
  ): Promise<ExpressCheckoutActionResult> =>
    this.toActionPromise(
      this.checkoutSwitchingService
        .addAddress$(
          this._checkout()?.checkout_key,
          { phone: address?.phone, email: address?.email } as ContactForm,
          {
            first_name: address?.firstName,
            last_name: address?.lastName,
            address: address?.address,
            address2: address?.address2,
            city: address?.city,
            state: address?.state,
            zipcode: address?.zipcode,
            country: address?.country,
          } as CommerceAddress,
          addressType?.toUpperCase() as ExpressCheckoutAddressType,
        )
        .pipe(switchMap((checkout) => this.requireCheckout$(checkout, 'Failed to save address'))),
    );

  public updateShipMethod = (
    shipMode: PaymentsShipModeRequest,
  ): Promise<ExpressCheckoutActionResult> => {
    const checkoutKey = this._checkout()?.checkout_key;
    if (!checkoutKey) {
      return Promise.reject(this.toActionError('No active checkout'));
    }
    if (!this.checkoutSwitchingService.isDirect()) {
      return Promise.reject(this.toActionError(SHIP_METHOD_UNAVAILABLE));
    }
    return this.toActionPromise(
      this.checkoutDirectDao.updateShippingMode(checkoutKey, shipMode.group_number ?? 1, {
        description: shipMode.description,
        shipMethod: shipMode.ship_method,
        unitPrice: shipMode.unit_price,
      }),
    );
  };

  public beginPaymentsSheet = (): void => {
    this.checkoutBeforeSheet = this._checkout();
  };

  /**
   * Mirrors SinglePageComponent.handleEventApplePayClosed(): the sheet saved its own address
   * on the order, so a cancel puts the previous one back.
   */
  public cancelPaymentsSheet = async (contact: ContactForm): Promise<void> => {
    const checkoutBeforeSheet = this.checkoutBeforeSheet;
    this.checkoutBeforeSheet = null;
    if (!checkoutBeforeSheet) {
      return;
    }
    this.setCheckout(checkoutBeforeSheet);

    const addresses = checkoutBeforeSheet.addresses;
    const previousAddress = addresses?.SHIPPINGBILLING ?? addresses?.SHIPPING;
    if (!previousAddress) {
      return;
    }
    const addressType = addresses?.SHIPPINGBILLING
      ? ExpressCheckoutAddressType.SHIPPING_BILLING
      : ExpressCheckoutAddressType.SHIPPING;

    await firstValueFrom(
      this.addAddress$(contact, previousAddress, addressType).pipe(catchError(() => of(null))),
    );
  };

  public getEstimatedDeliveryDates = (): Promise<{ status: 'SUCCESS'; edd?: EDDv2 }> =>
    Promise.resolve({
      status: 'SUCCESS' as const,
      edd: this._checkout()?.checkout_estimated_delivery,
    });

  public placeOrder = (
    authorizationResponse: AuthorizationResponse,
  ): Promise<ExpressCheckoutActionResult> => {
    const checkoutKey = this._checkout()?.checkout_key;
    if (!checkoutKey) {
      return Promise.reject(this.toActionError('No active checkout'));
    }
    return firstValueFrom(
      this.submitCheckout$().pipe(
        switchMap((confirmed) => this.requireCheckout$(confirmed, 'Failed to submit order')),
        tap(() =>
          this.paymentService.storePaymentMethodsInLocalStorage(
            authorizationResponse?.authorizationResponse?.paymentMethods,
            checkoutKey,
          ),
        ),
        // The order exists once submit succeeds, so a failed read-back must not reject:
        // payments would report a failure for an order that was placed.
        switchMap(() => this.getSubmittedCheckout$().pipe(catchError(() => of(null)))),
      ),
    )
      .then((submitted) => {
        this.expressCheckoutDrawerService.notifyOrderPlaced(submitted);
        return { status: 'SUCCESS' as const };
      })
      .catch((error: unknown) => {
        throw this.toActionError(error);
      });
  };

  public reportPaymentErrors = (errors: PaymentError[]): void =>
    this.expressCheckoutDrawerService.reportPaymentErrors(errors);

  private requireCheckout$(checkout: Checkout | null, message: string): Observable<Checkout> {
    return checkout ? of(checkout) : throwError(() => new Error(message));
  }

  private giftCardUnavailable$(message: string): Observable<Checkout> {
    return throwError(() => this.toActionError(message)).pipe(
      finalize(() => this.loadingOverlayService.hide()),
    );
  }

  private toActionPromise(source$: Observable<Checkout>): Promise<ExpressCheckoutActionResult> {
    return firstValueFrom(source$)
      .then((checkout) => ({ status: 'SUCCESS' as const, checkout }))
      .catch((error: unknown) => {
        throw this.toActionError(error);
      });
  }

  private toActionError(err: unknown): Error {
    const errors = this.toErrors(err);
    return Object.assign(new Error(errors[0]?.message ?? 'Bad Request'), {
      status: 'Fail' as const,
      errors,
    });
  }

  /** Normalizes a thrown error (or message string) into the `errors` contract shape. */
  private toErrors(err: unknown): Array<{ message: string }> {
    if (typeof err === 'string') {
      return [{ message: err }];
    }
    if (err && typeof err === 'object') {
      const maybeError = 'error' in err ? err.error : undefined;
      if (
        maybeError &&
        typeof maybeError === 'object' &&
        'message' in maybeError &&
        typeof maybeError.message === 'string'
      ) {
        return [{ message: maybeError.message }];
      }
      if ('message' in err && typeof err.message === 'string') {
        return [{ message: err.message }];
      }
    }
    // Express endpoints surface the user-facing message on err.error.message.
    const message = 'Bad Request';
    return [{ message }];
  }
}
